import { useState } from "react";
import { useRouter } from "next/navigation";
import { Copy } from "lucide-react";
import { Button } from "~/components/ui/button";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { getChartEditURL } from "~/routes";
import { Chart } from "~/modules/charts/types";
import { Modal } from "~/components/Modal";
import { ChartsUserEvents } from "~/modules/analytics/events/charts";
import { track } from "~/modules/analytics/track";
import { ChartsUpgradeModal } from "~/modules/charts/components/ChartsUpgradeModal";

// // // //

export function ChartCloneButton({ chart }: { chart: Chart }) {
    const router = useRouter();
    const { user, isPaidPlan } = useUser();
    const { addToaster } = useToaster();
    const [open, setOpen] = useState(false);

    // Cloning creates a new chart, which is a paid feature. Free users get the
    // upgrade prompt instead of the clone confirmation.
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const cloneChart = api.charts.clone.useMutation({
        onSuccess: (created) => {
            addToaster({
                variant: "success",
                message: `Chart "${created.title}" created.`,
            });
            router.push(getChartEditURL({ slug: created.slug }));
        },
        onError: (error) => {
            addToaster({
                variant: "danger",
                message: error.message || "Error cloning chart.",
            });
        },
    });

    const isCloning = cloneChart.isLoading;

    return (
        <>
            <Button
                variant="outline"
                className="gap-1.5 text-base"
                onClick={() => {
                    if (!isPaidPlan) {
                        setShowUpgradeModal(true);
                        track(
                            ChartsUserEvents.ChartIndividualCloneUpgradeView,
                            {
                                chart_slug: chart.slug,
                                chart_type: chart.type,
                            },
                        );
                        return;
                    }
                    setOpen(true);
                }}
            >
                <Copy className="size-5" />
                Clone
            </Button>

            <ChartsUpgradeModal
                show={showUpgradeModal}
                onHide={() => setShowUpgradeModal(false)}
                analytics={{
                    onConfirmed: () =>
                        track(
                            ChartsUserEvents.ChartIndividualCloneUpgradeViewPricing,
                            {
                                chart_slug: chart.slug,
                                chart_type: chart.type,
                            },
                        ),
                    onClose: () => {},
                }}
            />

            <Modal
                show={open}
                onHide={() => {
                    if (!isCloning) setOpen(false);
                }}
            >
                <div className="bg-card px-6 py-6 rounded-md">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">
                        Clone chart
                    </h3>
                    <p className="text-sm text-slate-500 mb-6">
                        Create a copy of &ldquo;{chart.title || chart.id}
                        &rdquo;? You&rsquo;ll be taken to the new chart to edit
                        it.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={isCloning}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primaryLight"
                            onClick={() => {
                                // Clone the chart
                                cloneChart.mutate({
                                    id: chart.id,
                                    organizationID: user.organization.id,
                                });

                                // Track the event
                                track(
                                    ChartsUserEvents.ChartIndividualClickClone,
                                    {
                                        chart_slug: chart.slug,
                                        chart_type: chart.type,
                                    },
                                );
                            }}
                            disabled={isCloning}
                        >
                            {isCloning ? "Cloning..." : "Clone"}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
